import {
  TruckRouteFuelResult,
  TruckRouteOption,
} from "../../../interfaces/Operations/truckRouteFuel";

export const fuelMoney = (amount: number, currency = "USD") =>
  `${currency} ${amount.toFixed(2)}`;
export const fuelDuration = (minutes: number) =>
  `${Math.floor(Math.ceil(minutes) / 60)}h ${Math.ceil(minutes) % 60}m`;
export const fuelDate = (date: string) =>
  new Date(date).toLocaleString(undefined, { timeZoneName: "short" });

export function buildDriverFuelMessage(
  result: TruckRouteFuelResult,
  selected: TruckRouteOption,
): string {
  const lines = [
    "TRUCK ROUTE & FUEL PLAN",
    `${result.startingLocation} → ${result.destination}`,
    `Fuel: ${result.fuelType}`,
    "",
    `SELECTED: ${selected.name}${selected.isRecommended ? " (recommended)" : ""}`,
  ];
  const plan = selected.fuelPlan;
  const roads = plan?.roadNames.length ? plan.roadNames : selected.roadNames;
  if (roads.length) lines.push(`Major roads: ${roads.join(" → ")}`);
  if (plan) {
    lines.push(
      `${plan.distanceMiles.toFixed(1)} mi · ${fuelDuration(plan.journeyMinutes)} including fuel stops`,
      `Estimated arrival: ${fuelDate(plan.estimatedArrivalUtc)}`,
      `Starting fuel: ${result.startingFuelGallons?.toFixed(2)} US gal · reserve: ${result.minimumReserveGallons.toFixed(2)} US gal`,
      `Trip consumption: ${plan.tripFuelGallons.toFixed(2)} US gal · fuel purchases: ${fuelMoney(plan.fuelPurchaseCost)}`,
      `Destination fuel: ${plan.remainingFuelGallons.toFixed(2)} US gal`,
      "",
      "FUEL STOPS IN TRAVEL ORDER",
    );
    if (!plan.stops.length)
      lines.push(
        "No fuel purchase needed with the entered starting fuel and reserve.",
      );
    plan.stops.forEach((stop, index) =>
      lines.push(
        `${index + 1}. ${stop.stationName} — ${stop.address}`,
        `   Arrive ${fuelDate(stop.estimatedArrivalUtc)} with ~${stop.arrivalFuelGallons.toFixed(2)} US gal.`,
        `   Buy ${stop.quantityGallons.toFixed(2)} US gal at ${fuelMoney(stop.pricePerGallon)}/US gal (~${fuelMoney(stop.purchaseCost)}).`,
        `   ${stop.explanation}`,
      ),
    );
  } else {
    lines.push(
      "Fuel quantities and costs could not be verified for this route. This is not a complete fuel plan.",
    );
    selected.stations
      .filter((station) => station.price != null)
      .slice(0, 5)
      .forEach((station) =>
        lines.push(
          `${station.name} — ${station.address}: ${fuelMoney(station.price!, station.currency ?? "Unknown currency")}/${station.priceUnit ?? "unknown unit"} (${station.priceStatus})`,
        ),
      );
  }
  lines.push("", "OTHER AVAILABLE ROUTES");
  const others = result.routes.filter((route) => route.id !== selected.id);
  if (!others.length) lines.push("No other route was returned.");
  others.forEach((route) => {
    const other = route.fuelPlan;
    lines.push(
      `${route.name}${route.isRecommended ? " (recommended)" : ""}: ${(other?.distanceMiles ?? route.distanceMiles).toFixed(1)} mi, ${fuelDuration(other?.journeyMinutes ?? route.travelTimeMinutes)}`,
      other
        ? `   Fuel ~${fuelMoney(other.fuelPurchaseCost)}; ${other.stops.length ? `stops: ${other.stops.map((stop) => `${stop.stationName} (${stop.quantityGallons.toFixed(2)} gal)`).join(" → ")}` : "no fuel purchase needed"}. ${route.withinTimeAllowance ? "Within" : "Outside"} time allowance.`
        : "   Fuel plan unavailable.",
    );
  });
  lines.push(
    "",
    "Estimates use reported station prices; confirm fuel grade and payable price before purchase. Follow truck restrictions and confirm station entrances. Rest breaks are not included. This message is a trip plan, not turn-by-turn navigation.",
  );
  const truckWarning = result.warnings.find((warning) =>
    warning.startsWith("Routes use truck mode."),
  );
  if (truckWarning) lines.push(truckWarning);
  if (result.missingInputs.length)
    lines.push(`Missing fuel setup: ${result.missingInputs.join(", ")}.`);
  if (selected.stationCoverageLimited)
    lines.push(
      "Station search coverage is limited; additional locations may exist.",
    );
  return lines.join("\n");
}
